import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting
 * @param {string} html - The HTML content to sanitize
 * @returns {string} - The sanitized HTML content
 */
export const sanitizeHTML = (html) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'del', 'ins',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'img',
      'blockquote', 'code', 'pre'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'target', 'rel'
    ],
    ADD_ATTR: ['rel'], // Add rel="noopener noreferrer" to links
    ALLOW_DATA_ATTR: false,
    FORBID_CONTENTS: ['script', 'style'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
    KEEP_CONTENT: true
  });
};

/**
 * Generates a plain text excerpt from HTML content
 * @param {string} html - The HTML content
 * @param {number} maxLength - Maximum length of excerpt (default: 150)
 * @returns {string} - Plain text excerpt
 */
export const generateExcerpt = (html, maxLength = 150) => {
  if (!html) return '';
  
  // Strip HTML tags and get plain text
  const plainText = html.replace(/<[^>]*>/g, '').trim();
  
  if (plainText.length <= maxLength) {
    return plainText;
  }
  
  // Truncate at word boundary
  const truncated = plainText.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  if (lastSpace > 0) {
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated + '...';
};

/**
 * Validates HTML content for basic requirements
 * @param {string} html - The HTML content to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateContent = (html) => {
  const errors = [];
  
  if (!html || html.trim().length === 0) {
    errors.push('Content cannot be empty');
  }
  
  const plainText = html.replace(/<[^>]*>/g, '').trim();
  if (plainText.length < 10) {
    errors.push('Content must be at least 10 characters long');
  }
  
  if (plainText.length > 50000) {
    errors.push('Content cannot exceed 50,000 characters');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
